import AddIcon from "@mui/icons-material/Add";
import MessageIcon from "@mui/icons-material/Message";
import ShareIcon from "@mui/icons-material/Share";
import { Theme } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Hit } from "_shared/_api/search/search-results";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import { skipToken } from "@reduxjs/toolkit/query";
import { MarketplaceResultType } from "config";
import { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import TeamBookmarkButton from "marketplace/bookmarks/_views/team-bookmark-button";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import { MarketplaceIndividualInfoJSON } from "marketplace/_schema/marketplace-individual-info-schema";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import MarketplaceTeamInfoAPIResponse from "marketplace/team-profile/_api/response-contracts/marketplace-team-info-api-response";
import ViewTeamProfile from "marketplace/team-profile/_views/view-team-profile";
import { useGetTeamAvatarQuery, useContactTeamMutation } from "marketplace/teams/_api/marketplace-team-api";
import Team from "marketplace/teams/team";
import ChatWindow from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSelector } from "react-redux";
import { NavigateFunction } from "react-router";
import Session from "users/__session/session";
import { selectIsNetworkedWith } from "users/networking/connections/_selectors/user-network-connection-selectors";
import NetworkButton from "users/networking/connections/_views/components/network-button";
import DirectProposalButton from "work/proposals/_views/_buttons/direct-proposal-button";

const ResultCard = styled(Card)(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    display: "block",
  },
  display: "flex",
  flex: "1 0 auto",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
  "&:first-of-type": {
    marginTop: 0,
  },
}));
const ResultHeader = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "grid",
  columnGap: theme.spacing(2),
  gridTemplateColumns: "auto 1fr",
}));
const ResultCardContent = styled(CardContent)(({ theme }) => ({
  flexGrow: "inherit",
  padding: theme.spacing(2),
  paddingLeft: theme.spacing(3),
}));
const ResultCardActions = styled(CardActions)(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    alignContent: "flex-end",
    alignItems: "unset",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down("lg")]: {
    alignContent: "flex-start",
    alignItems: "unset",
    flexDirection: "row",
    justifyContent: "flex-start",
    marginBottom: theme.spacing(2),
  },
  alignContent: "center",
  alignItems: "flex-end",
  flex: 0,
  flexDirection: "column",
  flexWrap: "wrap",
  justifyContent: "space-evenly",
  paddingRight: theme.spacing(3),
}));
const ResultName = styled(Typography)(() => ({
  cursor: "pointer",
  textDecoration: "underline",
  "& em": {
    backgroundColor: "rgba(255, 255, 0, 0.25)",
    fontStyle: "normal",
  },
}));
const TypeChip = styled(Chip)(() => ({
  borderColor: "#0a0",
  borderStyle: "dashed",
  color: "#0a0",
  opacity: 0.66,
}));
const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(30),
}));
const StyledNetworkButton = styled(NetworkButton)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(30),
}));
const StyledBookmarkButton = styled(TeamBookmarkButton)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(30),
}));
const Subtitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));
const ChatCardContent = styled(CardContent)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  width: "100%",
}));
const Details = styled("div")(({ theme }) => ({
  [theme.breakpoints.up(1920)]: {
    columnCount: 2,
  },
  [theme.breakpoints.down(1920)]: {
    columnCount: 1,
  },
  [theme.breakpoints.down("xl")]: {
    columnCount: 2,
  },
  [theme.breakpoints.down("lg")]: {
    columnCount: 2,
  },
  [theme.breakpoints.down("md")]: {
    columnCount: 1,
  },
  columnGap: theme.spacing(2),
  columnFill: "auto",
  marginTop: theme.spacing(2),
  "& em": {
    backgroundColor: "rgba(255, 255, 0, 0.25)",
    fontStyle: "normal",
  },
}));
const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.black,
  cursor: "pointer",
  textDecoration: "underline",
})) as typeof Link;
const SmallAvatar = styled(Avatar)(({ theme }) => ({
  cursor: "pointer",
  height: theme.spacing(10),
  width: theme.spacing(10),
}));
const ResultAvatarCardMedia = styled(CardMedia)(() => ({
  cursor: "pointer",
  height: "auto",
  width: "15vw",
})) as typeof CardMedia;
const ResultAvatar = styled(Avatar)(() => ({
  cursor: "pointer",
  height: "auto",
  width: "15vw",
}));
const GridRow = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  marginBottom: theme.spacing(1),
}));
const MemberContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(2),
  gridAutoFlow: "row",
}));
const MemberName = styled("div")(() => ({
  whiteSpace: "nowrap",
}));
const SelectTeamButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));
const NameContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
}));

export interface TeamResultProps {
  hit: Hit<MarketplaceSearchInfoJSON>;
  result: MarketplaceTeamInfoAPIResponse;
  embedded: boolean;
  session: Session;
  navigate?: NavigateFunction;
  getText: (
    result: Hit<MarketplaceSearchInfoJSON>,
    fieldNames: string[],
    base?: unknown,
    noCommas?: boolean,
  ) => { __html: string | TrustedHTML };
  handleResultClicked: (
    event: React.MouseEvent,
    profileId: Guid,
    profileType: MarketplaceResultType,
    component: React.JSX.Element,
    fullWidth?: boolean,
  ) => void;
  onTeamSelected?: (team: Team) => void;
}

export default function TeamResult(props: Readonly<TeamResultProps>) {
  const { hit, result, embedded, session, navigate, getText, handleResultClicked, onTeamSelected } = props;

  const teamId = result?.id ? new Guid(result.id) : null;
  const leaderId = result?.leader?.userId ? new Guid(result.leader.userId) : null;

  const [showChat, setShowChat] = React.useState<boolean>(false);
  const [forum, setForum] = React.useState<Forum>();

  const isXlDownDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));
  const isXlUpDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.up("xl"));

  // #region Selectors
  const { data: avatar } = useGetTeamAvatarQuery(
    teamId && result.avatar ? { teamId: teamId.value } : skipToken,
  );
  const [triggerContactTeam] = useContactTeamMutation();
  const isNetworkedWithLeader = useSelector(selectIsNetworkedWith(leaderId ?? undefined));

  const leaderProfileLink = getProfileLink(
    MarketplaceResultType.Individual,
    ["firstName", "lastName"],
    result?.leader,
    result?.leader?.id,
    true,
  );
  const leaderCompanyProfileLink = getProfileLink(
    MarketplaceResultType.Company,
    ["companyName"],
    result?.leader,
    result?.leader?.companyId,
    true,
    true,
  );
  const memberProfileLink = (member: MarketplaceIndividualInfoJSON) =>
    getProfileLink(MarketplaceResultType.Individual, ["firstName", "lastName"], member, member?.id, true);
  const memberCompanyProfileLink = (member: MarketplaceIndividualInfoJSON) =>
    getProfileLink(MarketplaceResultType.Company, ["companyName"], member, member?.companyId, true, true);

  async function contactTeam() {
    if (!result?.id) throw new Error("teamId is required to contact team.");
    try {
      const forumJson = await triggerContactTeam({ teamId: result.id }).unwrap();
      setForum(Forum.fromJSON(forumJson));
      setShowChat(true);
    } catch (error) {
      console.error(error);
    }
  }

  function getAddButton(result: MarketplaceTeamInfoAPIResponse) {
    if (!onTeamSelected) return null;

    return (
      <SelectTeamButton
        variant="outlined"
        color="primary"
        aria-label="Select Team"
        startIcon={<AddIcon />}
        disableElevation
        onClick={() => onTeamSelected(result.deserializeToTeam())}
      >
        Select Team
      </SelectTeamButton>
    );
  }

  function getProfileLink(
    type: MarketplaceResultType,
    fields: string[],
    base: unknown,
    profileId: string | null | undefined,
    noCommas: boolean = false,
    secondary: boolean = false,
  ) {
    if (!profileId) return null;
    return (
      <StyledLink
        style={{
          fontSize: secondary ? "0.85em" : "1em",
          opacity: secondary ? 0.6 : 1,
        }}
        component={Typography}
        dangerouslySetInnerHTML={getText(hit, fields, base, noCommas)}
        onClick={(event) => {
          if (type === MarketplaceResultType.Individual) return openIndividualProfile(event, profileId);
          if (type === MarketplaceResultType.Company) return openCompanyProfile(event, profileId);
          openTeamProfile(event, profileId);
        }}
      />
    );
  }

  function openTeamProfile(event: React.MouseEvent, id: string | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(event, profileId, MarketplaceResultType.Team, <ViewTeamProfile teamId={profileId} />, true);
  }

  function openIndividualProfile(event: React.MouseEvent, id: string | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Individual,
      <ViewIndividualProfile individualId={profileId} />,
      true,
    );
  }

  function openCompanyProfile(event: React.MouseEvent, id: string | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Company,
      <ViewCompanyProfile companyId={profileId} />,
      true,
    );
  }

  function showProposalButtons(): boolean {
    if (!navigate) return false;
    return result?.id !== undefined && result?.leader?.userId !== session?.user?.id?.value;
  }

  function handleSendProposalToResult(targetUrl: URL): void {
    navigate?.(targetUrl.pathname + targetUrl.search);
  }

  return (
    <React.Fragment>
      {showChat && (
        <ResultCard raised={false} variant="outlined">
          <ChatCardContent>
            <ChatWindow forums={forum ? [forum] : []} onClose={() => setShowChat(false)} />
          </ChatCardContent>
        </ResultCard>
      )}
      {!showChat && (
        <ResultCard raised={false} variant="outlined">
          {isXlUpDisplaySize && (
            <>
              <ResultAvatarCardMedia
                style={{ display: avatar ? undefined : "none" }}
                component="img"
                image={avatar}
                onClick={(event) => openTeamProfile(event, result?.id)}
              />
              <ResultAvatar
                style={{ display: avatar ? "none" : undefined }}
                variant="square"
                onClick={(event) => openTeamProfile(event, result?.id)}
              />
            </>
          )}
          <ResultCardContent>
            <ResultHeader>
              {isXlDownDisplaySize && (
                <span>
                  <SmallAvatar
                    style={{ display: avatar ? undefined : "none" }}
                    variant="circular"
                    src={avatar}
                    onClick={(event) => openTeamProfile(event, result?.id)}
                  />
                  <SmallAvatar
                    style={{ display: avatar ? "none" : undefined }}
                    variant="circular"
                    onClick={(event) => openTeamProfile(event, result?.id)}
                  />
                </span>
              )}
              <span>
                <NameContainer>
                  <ResultName
                    variant="h5"
                    dangerouslySetInnerHTML={getText(hit, ["name"], result, true)}
                    onClick={(event) => openTeamProfile(event, result?.id)}
                  />
                  {embedded && getAddButton(result)}
                </NameContainer>
                <TypeChip label="Team" variant="outlined" size="small" />
              </span>
            </ResultHeader>
            <Details>
              {result?.description && (
                <GridRow>
                  <Subtitle variant="body1">About Us:</Subtitle>
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: (() => {
                        const description = getText(hit, ["description"]).__html as string;
                        return description.length > 250 ? `${description.substring(0, 250)}...` : description;
                      })(),
                    }}
                  />
                </GridRow>
              )}
              {result?.leader?.firstName && result?.leader?.lastName && (
                <GridRow>
                  <Subtitle variant="body1">Leader:</Subtitle>
                  {leaderProfileLink} {leaderCompanyProfileLink}
                </GridRow>
              )}
              <GridRow>
                <Subtitle variant="body1">{`${result.members?.length.toString() ?? "0"} Members:`}</Subtitle>
                <MemberContainer>
                  {result?.members?.map((member: MarketplaceIndividualInfoJSON) => (
                    <MemberName key={member.id}>
                      {memberProfileLink(member)} {memberCompanyProfileLink(member)}
                    </MemberName>
                  ))}
                </MemberContainer>
              </GridRow>
            </Details>
          </ResultCardContent>
          <ResultCardActions disableSpacing>
            {!embedded && (
              <>
                {leaderId && <StyledNetworkButton userId={leaderId} />}
                <StyledBookmarkButton teamId={result.id ? new Guid(result.id) : undefined} />
                <Tooltip
                  title={isNetworkedWithLeader ? "" : "You must be in a vendor-team's network before contacting them"}
                >
                  <div style={{ display: "contents", width: "100%" }}>
                    <StyledButton
                      startIcon={<MessageIcon />}
                      variant="outlined"
                      disabled={!isNetworkedWithLeader}
                      onClick={contactTeam}
                    >
                      Send Message
                    </StyledButton>
                  </div>
                </Tooltip>
                <StyledButton
                  aria-label="share"
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={() => {
                    navigator.clipboard
                      .writeText(`${globalThis.location.origin}/marketplace/team/${result?.id}`)
                      .then(() => {
                        enqueueSnackbar("Link copied to clipboard", {
                          variant: "info",
                        });
                      })
                      .catch(() => {
                        enqueueSnackbar("Failed to copy link to clipboard", {
                          variant: "error",
                        });
                      });
                  }}
                >
                  Share
                </StyledButton>
                {showProposalButtons() && (
                  <>
                    <DirectProposalButton
                      targetName={Name.create({
                        params: { firstName: result.leader?.firstName ?? "", lastName: result.leader?.lastName ?? "" },
                      })}
                      targetTeamId={result.id ? new Guid(result.id) : undefined}
                      targetTeamName={result?.name}
                      onClick={handleSendProposalToResult}
                      useStyledButton={true}
                      action={"create"}
                    />
                    <DirectProposalButton
                      targetName={Name.create({
                        params: { firstName: result.leader?.firstName ?? "", lastName: result.leader?.lastName ?? "" },
                      })}
                      targetTeamId={result.id ? new Guid(result.id) : undefined}
                      targetTeamName={result?.name}
                      onClick={handleSendProposalToResult}
                      useStyledButton={true}
                      action={"request"}
                    />
                  </>
                )}
              </>
            )}
          </ResultCardActions>
        </ResultCard>
      )}
    </React.Fragment>
  );
}
